import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Store, ArrowRight, User } from "lucide-react";
import { hasApprovedMarketplaceAccess } from "../utils/marketplaceAccess";

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const hasSellerAccount = hasApprovedMarketplaceAccess(user);

  useEffect(() => {
    if (!hasSellerAccount) {
      navigate("/tenant", { replace: true });
    }
  }, [hasSellerAccount, navigate]);

  if (!hasSellerAccount) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-violet-500">
            Marketplace Access
          </p>
          <h1 className="mt-2 text-3xl font-black text-gray-900 dark:text-gray-100">
            Seller account state
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            There is no public seller-directory endpoint in the current backend
            contract. This page now reflects only your own seller access state,
            current plan, market assignment, and entry points into supported
            seller tools.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => navigate("/dashboard/products")}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
            Manage products <ArrowRight size={15} />
          </button>
          <button
            type="button"
            onClick={() => navigate("/dashboard/profile")}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800">
            Edit profile <User size={15} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MarketplaceCard
          label="Shop"
          value={user?.shopName || user?.name || "Seller account"}
          hint={user?.marketName || "Market assigned from seller profile"}
        />
        <MarketplaceCard
          label="Plan"
          value={user?.planName || user?.plan || "Current plan"}
          hint={user?.planMaxProducts ? `${user.planMaxProducts} max products` : "Plan details loaded from seller profile"}
        />
        <MarketplaceCard
          label="Categories"
          value={String(user?.nicheCategories?.length || 0)}
          hint={user?.planMaxCategories ? `${user.planMaxCategories} allowed` : "Approved niche categories"}
        />
        <MarketplaceCard
          label="Status"
          value={user?.isBanned ? "Restricted" : "Active"}
          hint={user?.verified ? "Provisioned seller" : "Seller profile available"}
        />
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-start gap-3">
          <Store size={18} className="mt-0.5 text-violet-500" />
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Why this page changed
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              The previous implementation depended on `GET /market-management/requests`,
              which is admin-only in the backend contract. The seller frontend now
              avoids that unsupported listing flow and keeps this route limited to
              seller-account state you are allowed to read.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketplaceCard({ label, value, hint }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
        {value}
      </p>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>
    </article>
  );
}
